import React from 'react';
import RSVPForm from './RSVPForm';
import VinylPlayer from './VinylPlayer';
import { Heart, Calendar, MapPin, Clock } from 'lucide-react';

export default function ArtisticCollage({ 
  data,
  onSubmitRSVP,
  isSubmitting,
  submitSuccess,
  isPreview = false
}) {
  const {
    couple_names = 'John & Jane',
    event_date = '',
    event_time = '',
    venue_name = '',
    venue_address = '',
    story_text = '',
    timeline = [],
    hero_image_url = '',
    hero_photos = [],
    hero_layout = 'fullwidth',
    gallery_images = [],
    color_theme = {},
    font_style = 'serif',
    background_music_url = '',
    song_title = '',
    artist_name = '',
    vinyl_cover_image = ''
  } = data || {};

  const bgColor = color_theme.background || '#e8e4df';
  const primaryColor = color_theme.primary || '#2c2c2c';
  const secondaryColor = color_theme.secondary || '#6b6560';
  const accentColor = color_theme.accent || '#8b7d6b';

  const fontClass = {
    serif: 'font-serif',
    sans: 'font-sans',
    script: 'font-serif'
  }[font_style] || 'font-serif';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Get hero photo - prefer hero_photos array, fall back to hero_image_url
  const getMainPhoto = () => {
    if (hero_photos && hero_photos.length > 0) return hero_photos[0];
    if (hero_image_url) return hero_image_url;
    return null;
  };

  const mainPhoto = getMainPhoto();
  // Content/story photos from gallery_images
  const storyPhotoLeft = gallery_images?.[0] || null;
  const storyPhotoRight = gallery_images?.[1] || null;
  // Extra gallery photos (after the first 2 used for story)
  const extraGallery = gallery_images?.slice(2) || [];

  return (
    <div className={`min-h-screen ${fontClass}`} style={{ backgroundColor: bgColor }}>
      
      {/* ============ HERO SECTION ============ */}
      <div className="relative overflow-hidden" style={{ minHeight: mainPhoto ? '380px' : '200px' }}>
        {/* Atmospheric background */}
        {mainPhoto && (
          <>
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ 
                backgroundImage: `url(${mainPhoto})`,
                filter: 'brightness(0.5) saturate(0.6)',
                transform: 'scale(1.1)'
              }}
            />
            <div 
              className="absolute inset-0"
              style={{
                background: `linear-gradient(180deg, ${bgColor}40 0%, transparent 30%, ${bgColor}dd 85%, ${bgColor} 100%)`
              }}
            />
          </>
        )}

        {/* Grain texture overlay */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-[0.08]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
          }}
        />

        {/* Hero photo - square, straight */}
        {mainPhoto && (
          <div className="relative z-10 flex justify-center pt-8 pb-6 px-6">
            <div 
              className="bg-white p-2 pb-8 shadow-xl"
              style={{ 
                maxWidth: '240px',
                width: '70%'
              }}
            >
              <img 
                src={mainPhoto} 
                alt="Our Story"
                className="w-full aspect-square object-cover"
              />
              {/* Caption under photo */}
              <p 
                className="text-center mt-2 text-xs italic"
                style={{ 
                  color: secondaryColor,
                  fontFamily: 'Georgia, serif'
                }}
              >
                {couple_names || 'Our beginning'}
              </p>
            </div>
          </div>
        )}

        {/* If no photo, show text header */}
        {!mainPhoto && (
          <div className="relative z-10 text-center py-16 px-6">
            <p 
              className="text-[10px] tracking-[0.4em] uppercase mb-4"
              style={{ color: accentColor }}
            >
              Together with their families
            </p>
            <h1 
              className="text-3xl font-light tracking-wider"
              style={{ color: primaryColor }}
            >
              {couple_names}
            </h1>
          </div>
        )}
      </div>

      {/* ============ COUPLE NAMES & INVITATION ============ */}
      <div className="text-center px-6 pt-6 pb-4" style={{ backgroundColor: bgColor }}>
        {mainPhoto && (
          <>
            <p 
              className="text-[10px] tracking-[0.35em] uppercase mb-3"
              style={{ color: accentColor }}
            >
              Together with their families
            </p>
            <h1 
              className="text-3xl font-light tracking-wider mb-2"
              style={{ color: primaryColor }}
            >
              {couple_names}
            </h1>
          </>
        )}
        <p 
          className="text-xs mt-2 leading-relaxed"
          style={{ color: secondaryColor }}
        >
          Request the pleasure of your company<br />at their wedding celebration
        </p>
      </div>

      {/* Decorative divider */}
      <div className="flex items-center justify-center gap-3 py-4 px-6" style={{ backgroundColor: bgColor }}>
        <div className="w-12 h-px" style={{ backgroundColor: `${accentColor}50` }} />
        <Heart className="w-3 h-3" style={{ color: accentColor }} />
        <div className="w-12 h-px" style={{ backgroundColor: `${accentColor}50` }} />
      </div>

      {/* ============ EVENT DETAILS ============ */}
      <div className="text-center px-6 pb-6" style={{ backgroundColor: bgColor }}>
        {event_date && (
          <div className="mb-4">
            <p 
              className="text-[10px] tracking-[0.3em] uppercase mb-2"
              style={{ color: accentColor }}
            >
              Wedding Ceremony
            </p>
            <div className="flex items-center justify-center gap-2 mb-1">
              <Calendar className="w-3 h-3" style={{ color: accentColor }} />
              <p className="text-sm" style={{ color: primaryColor }}>
                {formatDate(event_date)}
              </p>
            </div>
            {event_time && (
              <div className="flex items-center justify-center gap-2">
                <Clock className="w-3 h-3" style={{ color: accentColor }} />
                <p className="text-sm" style={{ color: secondaryColor }}>{event_time}</p>
              </div>
            )}
          </div>
        )}

        {(venue_name || venue_address) && (
          <div className="flex items-center justify-center gap-2">
            <MapPin className="w-3 h-3" style={{ color: accentColor }} />
            <div>
              {venue_name && <p className="text-sm" style={{ color: primaryColor }}>{venue_name}</p>}
              {venue_address && <p className="text-xs" style={{ color: secondaryColor }}>{venue_address}</p>}
            </div>
          </div>
        )}
      </div>

      {/* ============ STORY COLLAGE SECTION ============ */}
      {(storyPhotoLeft || storyPhotoRight || story_text) && (
        <div className="relative px-4 py-8" style={{ backgroundColor: bgColor }}>
          {/* Section header */}
          {story_text && (
            <p 
              className="text-[10px] tracking-[0.3em] uppercase text-center mb-6"
              style={{ color: accentColor }}
            >
              Our Story
            </p>
          )}

          {/* Asymmetric collage: small left + text right, then big right + text left */}
          {(storyPhotoLeft || storyPhotoRight) && (
            <div className="space-y-6 mb-6">
              {/* Row 1: Small photo LEFT + text RIGHT */}
              {storyPhotoLeft && (
                <div className="flex gap-3 items-start">
                  <div className="w-[42%] flex-shrink-0">
                    <img 
                      src={storyPhotoLeft} 
                      alt="Our moment"
                      className="w-full aspect-[3/4] object-cover"
                    />
                  </div>
                  <div className="flex-1 flex items-center">
                    {story_text ? (
                      <p 
                        className="text-xs leading-relaxed"
                        style={{ 
                          color: secondaryColor,
                          fontFamily: '"Courier New", Courier, monospace',
                          lineHeight: '1.8'
                        }}
                      >
                        {story_text}
                      </p>
                    ) : (
                      <p className="text-xs italic" style={{ color: `${secondaryColor}80` }}>
                        Your love story...
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Row 2: Text LEFT + big photo RIGHT (portrait, offset down) */}
              {storyPhotoRight && (
                <div className="flex gap-3 items-start">
                  <div className="w-[40%] flex-shrink-0 flex items-end pt-8">
                    <p 
                      className="text-xs leading-relaxed"
                      style={{ 
                        color: secondaryColor,
                        fontFamily: 'Georgia, serif',
                        fontStyle: 'italic',
                        lineHeight: '1.8'
                      }}
                    >
                      {couple_names}
                    </p>
                  </div>
                  <div className="flex-1">
                    <img 
                      src={storyPhotoRight} 
                      alt="Our celebration"
                      className="w-full aspect-[3/4] object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {/* ============ TIMELINE ============ */}
      {timeline && timeline.length > 0 && (
        <div className="px-6 py-8" style={{ backgroundColor: bgColor }}>
          <p 
            className="text-[10px] tracking-[0.3em] uppercase text-center mb-6"
            style={{ color: accentColor }}
          >
            Timeline
          </p>
          <div className="space-y-4 max-w-xs mx-auto">
            {timeline.map((item, idx) => (
              <div 
                key={idx} 
                className="flex items-start gap-4"
              >
                <div className="flex flex-col items-center">
                  <div 
                    className="w-2 h-2 rounded-full mt-1"
                    style={{ backgroundColor: accentColor }}
                  />
                  {idx < timeline.length - 1 && (
                    <div className="w-px h-8" style={{ backgroundColor: `${accentColor}30` }} />
                  )}
                </div>
                <div className="flex-1 -mt-0.5">
                  <span className="text-xs" style={{ color: secondaryColor }}>{item.time}</span>
                  <p className="text-sm" style={{ color: primaryColor }}>{item.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============ EXTRA GALLERY ============ */}
      {extraGallery.length > 0 && (
        <div className="px-4 py-6" style={{ backgroundColor: bgColor }}>
          <p 
            className="text-[10px] tracking-[0.3em] uppercase text-center mb-5"
            style={{ color: accentColor }}
          >
            Gallery
          </p>
          <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1 snap-x">
            {extraGallery.slice(0, 4).map((img, idx) => (
              <div 
                key={idx}
                className="flex-shrink-0 shadow-md snap-center"
                style={{ width: '100px' }}
              >
                <img 
                  src={img} 
                  alt={`Gallery ${idx + 1}`}
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== VINYL PLAYER ===== */}
      {background_music_url && (
        <VinylPlayer
          audioSrc={background_music_url}
          coverImage={vinyl_cover_image || getMainPhoto()}
          songTitle={song_title}
          artistName={artist_name}
          accentColor={accentColor}
          bgColor={bgColor}
          isPreview={isPreview}
        />
      )}

      {/* ============ RSVP SECTION ============ */}
      <div 
        className="relative px-6 py-10"
        style={{ backgroundColor: '#faf8f5' }}
      >
        {/* Subtle top border */}
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-px"
          style={{ backgroundColor: accentColor }}
        />
        
        <div className="text-center mb-6">
          <p 
            className="text-[10px] tracking-[0.3em] uppercase"
            style={{ color: accentColor }}
          >
            Kindly Respond
          </p>
        </div>

        {!isPreview ? (
          <RSVPForm
            onSubmit={onSubmitRSVP}
            colorTheme={{ ...color_theme, background: '#faf8f5' }}
            fontStyle={font_style}
            isSubmitting={isSubmitting}
            submitSuccess={submitSuccess}
          />
        ) : (
          <div 
            className="text-center py-6 border rounded-lg"
            style={{ borderColor: `${accentColor}30` }}
          >
            <p className="text-xs" style={{ color: secondaryColor }}>RSVP form will appear here</p>
          </div>
        )}
      </div>

      {/* ============ FOOTER ============ */}
      <div className="relative overflow-hidden py-10 px-6" style={{ backgroundColor: bgColor }}>
        {/* Grain texture */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
          }}
        />

        <div className="relative z-10 text-center">
          <p 
            className="text-xs tracking-wider mb-4"
            style={{ color: secondaryColor }}
          >
            We can't wait to celebrate with you
          </p>
          <div className="flex items-center justify-center gap-2">
            <div className="w-6 h-px" style={{ backgroundColor: `${accentColor}50` }} />
            <Heart className="w-3 h-3" style={{ color: accentColor }} />
            <div className="w-6 h-px" style={{ backgroundColor: `${accentColor}50` }} />
          </div>
        </div>
      </div>
    </div>
  );
}
